import React, { useMemo, useState } from 'react';
import { getOrgIdForTeamMode, getSelectedTeamIds } from '../../dux';
import { useSelector } from 'react-redux';
import ConfirmActionPopover from '../Shared/Popovers/ConfirmActionPopover';
import { faShareFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { t } from 'i18next';
import TeamSelector from '../Shared/TeamSelector';
import { BillAPI, TeamAPI } from '../../api';
import { BillAlias } from '@enview/interface/types/bills/BillSummary';
import { Bill } from '@enview/interface/types/bills/Bill';
import { State } from '../../dux/@types';

interface ShareBillAliasButtonProps {
  bill: Bill;
  alias: BillAlias;
  onSaveCallback?: () => void;
}

export const ShareBillAliasButton: React.FC<ShareBillAliasButtonProps> = ({
  onSaveCallback,
  alias,
  bill,
}) => {
  const [displayPopup, setDisplayPopup] = useState(false);
  const [setBillAlias] = BillAPI.endpoints.setBillAlias.useMutation();
  const selectedTeamIds = useSelector(getSelectedTeamIds);
  const teamModeOrgId = useSelector(getOrgIdForTeamMode);
  const orgUser = useSelector((state: State) => state.account.organizationUser);

  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);

  const onShareClicked = () => {
    setDisplayPopup(true);
  };

  const onSave = async () => {
    const aliasArray = selectedTeamIds.map((teamId) => {
      const { id, ...aliasWithoutId } = alias;
      void id;
      return { ...aliasWithoutId, teamId };
    });
    await setBillAlias({ bill, alias: aliasArray, reset: true });
    if (onSaveCallback) {
      onSaveCallback();
    }
    setDisplayPopup(false);
  };

  const teams = useMemo(
    () =>
      (teamMemberships || [])
        .map((tm) => tm.team)
        .filter(
          (team) =>
            team.organizationId === teamModeOrgId ||
            (orgUser && team.id === orgUser.teamId),
        ),
    [teamMemberships, teamModeOrgId],
  );

  const teamIds = new Set(teams.map((team) => team.id));
  const initialSelection =
    bill.userAliases
      ?.filter(
        (userAlias) => teamIds.has(userAlias.teamId) && userAlias.alias === alias.alias,
      )
      .map((userAlias) => userAlias.teamId) || [];

  const popoverContent = (
    <>
      <p className="popover-confirm-body font-weight-bold mt-2">
        {t('bill.sidebar.alias.ShareAliasPopoverTitle')}
      </p>
      <TeamSelector disableCurrentTeam initialSelection={initialSelection} />
      <button
        className="btn btn-sm btn-block btn-action mt-3"
        onClick={onSave}
        type="button"
      >
        Save
      </button>
    </>
  );

  const shareAliasButton = (
    <div onClick={onShareClicked}>
      <FontAwesomeIcon
        color="#919191"
        icon={faShareFromSquare}
        id="share-bill-alias-button"
        style={{ cursor: 'pointer' }}
      />
      <ReactTooltip
        anchorId="share-bill-alias-button"
        children={t('bill.sidebar.alias.shareAliasTooltip')}
        className="tooltip-content"
        clickable
        delayHide={500}
        place="right"
        style={{ maxWidth: '300px' }}
      />
    </div>
  );

  return (
    <div className="d-flex flex-column justify-content-center">
      <ConfirmActionPopover
        align="end"
        closePopup={() => setDisplayPopup(false)}
        content={popoverContent}
        positions={['bottom']}
        show={displayPopup}
        target={shareAliasButton}
        toggleCallback={() => {}}
      />
    </div>
  );
};

export default ShareBillAliasButton;
